/**
 * My Watches Types
 *
 * The registered-watch record returned by the My Account App
 * (`GET my-watches/me`). Display data (image, name, specs) is NOT on this
 * payload — it is hydrated from Saleor/Algolia by SKU. Certificate documents
 * come from PIM, flagged by the `has_*_cert` booleans.
 *
 * See the `my-account-backend` skill for the full contract + env/JWKS rule.
 */

/** Warranty status codes returned by `my-watches/warranty`. */
export enum WarrantyStatus {
  ActiveAndUnbound = 200,
  NotFound = 801,
  RegisteredBySelf = 810,
  RegisteredByOther = 811,
  BrandMismatch = 813,
}

/** A single registered watch as returned by the My Account App. */
export interface Watch {
  /** Serial number — the unique owned-instance key. */
  sn: string;
  /** Product SKU — drives Saleor/Algolia hydration; brand is derived from it. */
  sku: string;
  country?: string;
  channel?: string;
  /** Warranty activation date, e.g. "2020-10-13 00:00". */
  start_date?: string | null;
  /** Warranty expiry date, e.g. "2025-10-12 00:00". */
  end_date?: string | null;
  is_bind?: boolean;
  is_visible?: boolean;
  is_extendable?: boolean;
  is_extended?: boolean;
  /** ⛔ Unreliable — reports `false` for a card valid until 2034. Derive from `end_date`. */
  is_warranty_active?: boolean | null;
  has_cosc_cert?: boolean;
  has_jewelry_cert?: boolean;
  has_limit_edition_cert?: boolean;
  has_year_140_cert?: boolean;
  warranty_code?: string;
  product_name?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Envelope returned by the My Account App my-watches endpoints. */
export interface MyAccountEnvelope<T> {
  code?: number;
  type?: string;
  message?: string;
  msg?: string;
  data?: T;
}

/** Envelope returned by the My Account App my-watches endpoints. */
export type MyWatchesResponse = MyAccountEnvelope<Watch[]>;

/**
 * Envelope returned by `my-watches/warranty`. The payload is a single watch
 * record (the warranty holder's view of it): `sku` drives hydration/register,
 * `is_visible && !is_bind` means registerable. The numeric `code` carries the
 * warranty status (see {@link WarrantyStatus}).
 */
export type WarrantyLookupResponse = MyAccountEnvelope<Watch>;

/** Body of `POST my-watches/me` — the authenticated register call. */
export interface RegisterWatchPayload {
  sn: string;
  /** The product SKU. Brand scoping is passed as a query param on register. */
  model: string;
  country: string;
  product_name: string;
  /** QR security token, present when registration came from a warranty card. */
  sec?: string;
  channel?: string;
}

/**
 * Body of `POST backoffice/my-watches/unconfirmed/{email}` — registration for
 * a just-signed-up account that has not confirmed its email yet.
 */
export interface RegisterWatchUnconfirmedPayload extends RegisterWatchPayload {
  email: string;
}
